import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  ArrowLeftIcon,
  EnvelopeIcon,
  PhoneIcon,
  AcademicCapIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  LinkIcon
} from '@heroicons/react/24/solid';
import { useConfirm } from '../ConfirmDialog';

export default function TutorApplicationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');

  useEffect(() => {
    fetchApplication();
  }, [id]);

  const fetchApplication = async () => {
    try {
      const { data } = await axios.get('/api/admin/tutor-applications');
      // Backend returns { applications: [...] }
      const applicationsArray = data.applications || data || [];
      const foundApplication = applicationsArray.find(a => a.id === id);

      if (!foundApplication) {
        toast.error('Application not found');
        navigate('/admin/applications');
        return;
      }

      setApplication(foundApplication);
    } catch (error) {
      console.error('Error fetching application:', error);
      toast.error('Failed to load application details');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!(await confirm({
      title: 'Approve application?',
      message: 'A Solomon account will be created and the applicant will receive login instructions.',
      confirmLabel: 'Approve',
      danger: false,
    }))) {
      return;
    }

    setApproving(true);
    try {
      await axios.put(`/api/admin/approve-tutor/${id}`);
      toast.success('Tutor approved! Account created and welcome email sent.');
      fetchApplication(); // Refresh to show updated status
    } catch (error) {
      console.error('Error approving tutor:', error);
      toast.error(error.response?.data?.error || 'Failed to approve tutor');
    } finally {
      setApproving(false);
    }
  };

  const handleReject = async (e) => {
    e.preventDefault();
    setRejecting(true);

    try {
      await axios.put(`/api/admin/reject-tutor/${id}`, {
        rejection_reason: rejectionReason
      });

      toast.success('Application rejected');
      setShowRejectModal(false);
      fetchApplication(); // Refresh to show updated status
    } catch (error) {
      console.error('Error rejecting application:', error);
      toast.error(error.response?.data?.error || 'Failed to reject application');
    } finally {
      setRejecting(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
      reviewing: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      interview_scheduled: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
      approved: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      rejected: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
      withdrawn: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
    };

    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${styles[status] || styles.pending}`}>
        {status.replace('_', ' ').toUpperCase()}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!application) {
    return null;
  }

  return (
    <div className="max-w-7xl mx-auto p-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/admin/applications')}
          className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-4"
        >
          <ArrowLeftIcon className="h-5 w-5" />
          Back to Applications
        </button>

        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              {application.name}
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Applied {new Date(application.created_at).toLocaleDateString()}
            </p>
          </div>
          {getStatusBadge(application.status)}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Contact Information */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 border border-transparent dark:border-gray-700">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
              Contact Information
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">Email</div>
                <div className="flex items-center gap-2 text-gray-900 dark:text-white">
                  <EnvelopeIcon className="h-5 w-5 text-gray-400" />
                  <a href={`mailto:${application.email}`} className="hover:text-blue-500">
                    {application.email}
                  </a>
                </div>
              </div>

              <div>
                <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">Phone</div>
                <div className="flex items-center gap-2 text-gray-900 dark:text-white">
                  <PhoneIcon className="h-5 w-5 text-gray-400" />
                  <a href={`tel:${application.phone}`} className="hover:text-blue-500">
                    {application.phone}
                  </a>
                </div>
              </div>

              {application.linkedin_url && (
                <div className="md:col-span-2">
                  <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">LinkedIn</div>
                  <div className="flex items-center gap-2 text-gray-900 dark:text-white">
                    <LinkIcon className="h-5 w-5 text-gray-400" />
                    <a
                      href={application.linkedin_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:text-blue-500 truncate"
                    >
                      {application.linkedin_url}
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Qualifications */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 border border-transparent dark:border-gray-700">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
              Qualifications
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">Highest Qualification</div>
                <div className="font-semibold text-gray-900 dark:text-white">
                  {application.highest_qualification}
                </div>
              </div>

              <div>
                <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">University</div>
                <div className="font-semibold text-gray-900 dark:text-white">
                  {application.university}
                </div>
              </div>

              <div>
                <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">Field of Study</div>
                <div className="font-semibold text-gray-900 dark:text-white">
                  {application.field_of_study}
                </div>
              </div>

              <div>
                <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">Graduation Year</div>
                <div className="font-semibold text-gray-900 dark:text-white">
                  {application.graduation_year}
                </div>
              </div>
            </div>
          </div>

          {/* Teaching Experience */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 border border-transparent dark:border-gray-700">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
              Teaching Experience
            </h2>

            <div className="mb-4">
              <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">Years of Experience</div>
              <div className="font-semibold text-gray-900 dark:text-white">
                {application.years_experience}
              </div>
            </div>

            <div className="mb-4">
              <div className="text-sm text-gray-500 dark:text-gray-400 mb-2">Teaching Experience</div>
              <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg text-gray-900 dark:text-white whitespace-pre-wrap">
                {application.teaching_experience}
              </div>
            </div>

            {application.previous_tutoring && (
              <div>
                <div className="text-sm text-gray-500 dark:text-gray-400 mb-2">Previous Tutoring</div>
                <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg text-gray-900 dark:text-white whitespace-pre-wrap">
                  {application.previous_tutoring}
                </div>
              </div>
            )}
          </div>

          {/* Subjects & Grade Levels */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 border border-transparent dark:border-gray-700">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
              Subjects & Grade Levels
            </h2>

            <div className="mb-6">
              <div className="text-sm text-gray-500 dark:text-gray-400 mb-2">Subjects Can Teach</div>
              <div className="flex flex-wrap gap-2">
                {application.subjects_can_teach?.map((subject, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded-full text-sm font-semibold"
                  >
                    {subject}
                  </span>
                ))}
              </div>
            </div>

            <div>
              <div className="text-sm text-gray-500 dark:text-gray-400 mb-2">Grade Levels Can Teach</div>
              <div className="flex flex-wrap gap-2">
                {application.grade_levels_can_teach?.map((grade, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded text-sm"
                  >
                    {grade}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Availability */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 border border-transparent dark:border-gray-700">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
              Availability
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <div className="text-sm text-gray-500 dark:text-gray-400 mb-2">Available Days</div>
                <div className="flex flex-wrap gap-2">
                  {application.availability_days?.map((day, index) => (
                    <span
                      key={index}
                      className="px-2 py-1 bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 rounded text-sm"
                    >
                      {day}
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">Hours Per Week</div>
                <div className="font-semibold text-gray-900 dark:text-white">
                  {application.hours_per_week}
                </div>
              </div>
            </div>

            <div>
              <div className="text-sm text-gray-500 dark:text-gray-400 mb-2">Available Times</div>
              <div className="flex flex-wrap gap-2">
                {application.availability_times?.map((time, index) => (
                  <span
                    key={index}
                    className="px-2 py-1 bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 rounded text-sm"
                  >
                    {time}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Why Tutor */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 border border-transparent dark:border-gray-700">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
              Why They Want to Tutor
            </h2>
            <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg text-gray-900 dark:text-white whitespace-pre-wrap">
              {application.why_tutor}
            </div>
          </div>

          {/* References */}
          {application.reference_contacts && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 border border-transparent dark:border-gray-700">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                References
              </h2>
              <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg text-gray-900 dark:text-white whitespace-pre-wrap">
                {application.reference_contacts}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar Actions */}
        <div className="space-y-6">
          {/* Approval Actions */}
          {(application.status === 'pending' || application.status === 'reviewing') && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 border border-transparent dark:border-gray-700">
              <h3 className="font-bold text-gray-900 dark:text-white mb-4">
                Review Application
              </h3>

              <div className="space-y-3">
                <button
                  onClick={handleApprove}
                  disabled={approving}
                  className="w-full bg-green-500 text-white py-3 rounded-lg hover:bg-green-600 transition font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  <CheckCircleIcon className="h-5 w-5" />
                  {approving ? 'Approving...' : 'Approve Tutor'}
                </button>

                <button
                  onClick={() => setShowRejectModal(true)}
                  className="w-full bg-red-500 text-white py-3 rounded-lg hover:bg-red-600 transition font-semibold flex items-center justify-center gap-2"
                >
                  <XCircleIcon className="h-5 w-5" />
                  Reject Application
                </button>
              </div>

              <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900 rounded-lg text-sm text-blue-900 dark:text-blue-100">
                <strong>Approving will:</strong>
                <ul className="list-disc ml-5 mt-2 space-y-1">
                  <li>Create a Solomon tutor account</li>
                  <li>Send password setup email</li>
                  <li>Send approval notification</li>
                  <li>Add to available tutors pool</li>
                </ul>
              </div>
            </div>
          )}

          {/* Account Created */}
          {application.status === 'approved' && application.user_id && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 border border-transparent dark:border-gray-700">
              <h3 className="font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <CheckCircleIcon className="h-5 w-5 text-green-500" />
                Tutor Account Created
              </h3>
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Account created on {new Date(application.account_created_at).toLocaleDateString()}
              </div>
              <button
                onClick={() => navigate('/dashboard')}
                className="w-full bg-blue-500 text-white py-2 rounded-lg hover:bg-blue-600 transition"
              >
                View in Dashboard
              </button>
            </div>
          )}

          {/* Rejection Info */}
          {application.status === 'rejected' && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 border border-transparent dark:border-gray-700">
              <h3 className="font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <XCircleIcon className="h-5 w-5 text-red-500" />
                Application Rejected
              </h3>
              {application.rejection_reason && (
                <div>
                  <div className="text-sm text-gray-500 dark:text-gray-400 mb-2">Reason</div>
                  <div className="p-3 bg-red-50 dark:bg-red-900 rounded-lg text-red-900 dark:text-red-100 text-sm">
                    {application.rejection_reason}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Quick Actions */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 border border-transparent dark:border-gray-700">
            <h3 className="font-bold text-gray-900 dark:text-white mb-4">
              Quick Actions
            </h3>
            <div className="space-y-2">
              <a
                href={`mailto:${application.email}`}
                className="block w-full text-center bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white py-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition"
              >
                Email Applicant
              </a>
              <a
                href={`tel:${application.phone}`}
                className="block w-full text-center bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white py-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition"
              >
                Call Applicant
              </a>
              {application.linkedin_url && (
                <a
                  href={application.linkedin_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full text-center bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white py-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition"
                >
                  View LinkedIn
                </a>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              Reject Application
            </h2>

            <form onSubmit={handleReject} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Reason for Rejection (optional)
                </label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  rows={4}
                  placeholder="e.g., Qualifications do not meet requirements, Insufficient experience, etc."
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowRejectModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={rejecting}
                  className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {rejecting ? 'Rejecting...' : 'Reject'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
